import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import DownloadModalTables from "../../components/DownloadModalTables";
// Matches the shell used on DebtorDashboard. If your superadmin area uses a
// dedicated nav (e.g. NavigationPanelSuperAdmin), swap this import for that.
import NavigationPanel from "../../components/NavigationPanel";
import Footer from "../../components/Footer";
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { MoreVertical, ArrowRight } from "lucide-react";
import { getCurrentUser } from "../../utils/getCurrentUser";
import { companyService } from "../../services/companyService";

const SUBSCRIPTION_STATUS_LABELS = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Past due",
  canceled: "Canceled",
  incomplete: "Incomplete",
  unpaid: "Unpaid",
};

const STATUS_COLOR_MAP = {
  Active: { bg: "#ECFDF5", color: "#059669" },
  Trialing: { bg: "#EFF6FF", color: "#2563EB" },
  "Past due": { bg: "#FFFBEB", color: "#D97706" },
  Canceled: { bg: "#FEF2F2", color: "#DC2626" },
  Incomplete: { bg: "#F1F5F9", color: "#64748B" },
  Unpaid: { bg: "#FFFBEB", color: "#D97706" },
  Suspended: { bg: "#FEF2F2", color: "#DC2626" },
};

const PIE_COLORS = ["#2563EB", "#059669", "#D97706", "#7C3AED", "#DC2626", "#64748B"];

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage] = useState(10);
  const [openMenuId, setOpenMenuId] = useState(null);

  const closeLogoutConfirmation = useCallback(() => setLogoutConfirmationOpen(false), []);
  const openDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(true), []);
  const closeDownloadModalTables = useCallback(() => setDownloadModalTablesOpen(false), []);

  // ── Fetch companies ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const response = await companyService.getAllCompanies();

        const mapped = response.map((c) => ({
          id: c.id,
          companyName: c.companyName,
          companyEmail: c.companyEmail,
          accountType: c.accountType,
          autoIncrementAccount: c.autoIncrementAccount,
          subscriptionStatus: c.subscriptionStatus,
          maxLicenses: c.maxLicenses,
          usedLicenses: c.usedLicenses,
          isActive: c.isActive,
          isSuspended: c.isSuspended,
          suspensionReason: c.suspensionReason,
          platformFeeEnabled: c.platformFeeEnabled,
          activePaymentProvider: c.activePaymentProvider,
          nextBillingDate: c.nextBillingDate
            ? new Date(c.nextBillingDate).toLocaleDateString("en-GB")
            : "—",
          signedUp: c.createdAt
            ? new Date(c.createdAt).toLocaleDateString("en-GB")
            : "—",
          ownerEmail: c.owner?.email || c.companyEmail,
          lbaFormsCount: c.lbaForms?.length ?? c.lbaFormsCount ?? 0,
        }));

        setCompanies(mapped);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch companies:", err);
        setError("Failed to load companies. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  // ── Stats ────────────────────────────────────────────────────────────
  const totalCompanies = companies.length;
  const activeSubscriptions = companies.filter((c) => c.subscriptionStatus === "active").length;
  const suspendedCompanies = companies.filter((c) => c.isSuspended).length;
  const totalLicensesUsed = companies.reduce((sum, c) => sum + (c.usedLicenses || 0), 0);

  const metricCards = [
    {
      label: "Total Companies",
      value: totalCompanies,
      format: "count",
      accentBar: "#2563EB",
      iconBg: "#EFF6FF",
      iconColor: "#2563EB",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1m4 0h1m-6 4h1m4 0h1m-6 4h1m4 0h1" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Active Subscriptions",
      value: activeSubscriptions,
      format: "count",
      accentBar: "#059669",
      iconBg: "#ECFDF5",
      iconColor: "#059669",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 4 12 14.01l-3-3" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Suspended",
      value: suspendedCompanies,
      format: "count",
      accentBar: "#DC2626",
      iconBg: "#FEF2F2",
      iconColor: "#DC2626",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 9v5M12 21.41H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12v-.01Z" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11.995 17h.009" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      label: "Licenses In Use",
      value: totalLicensesUsed,
      format: "count",
      accentBar: "#7C3AED",
      iconBg: "#F5F3FF",
      iconColor: "#7C3AED",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M17 20h5v-2a4 4 0 0 0-3-3.87M9 20H4v-2a4 4 0 0 1 3-3.87M13.5 8.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0ZM20 8.5a3.5 3.5 0 0 1-2.5 3.36" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  // ── Pie chart: subscription status breakdown ────────────────────────
  const statusCounts = companies.reduce((acc, c) => {
    const label = c.isSuspended
      ? "Suspended"
      : SUBSCRIPTION_STATUS_LABELS[c.subscriptionStatus] || c.subscriptionStatus;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  // ── Filtering + search ───────────────────────────────────────────────
  const filteredCompanies = companies.filter((c) => {
    let statusMatch = true;
    if (statusFilter === "suspended") {
      statusMatch = c.isSuspended;
    } else if (statusFilter !== "all") {
      statusMatch = c.subscriptionStatus === statusFilter && !c.isSuspended;
    }

    const q = searchQuery.toLowerCase();
    const searchMatch =
      q === "" ||
      c.companyName?.toLowerCase().includes(q) ||
      c.companyEmail?.toLowerCase().includes(q) ||
      c.autoIncrementAccount?.toLowerCase().includes(q);

    return statusMatch && searchMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredCompanies.length / companiesPerPage));
  const indexOfLast = currentPage * companiesPerPage;
  const indexOfFirst = indexOfLast - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleToggleSuspend = async (company) => {
    try {
      await companyService.setSuspended(company.id, !company.isSuspended);
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, isSuspended: !c.isSuspended } : c))
      );
    } catch (err) {
      console.error("Failed to update suspension status:", err);
    } finally {
      setOpenMenuId(null);
    }
  };

  const handleTogglePlatformFee = async (company) => {
    try {
      await companyService.setPlatformFeeEnabled(company.id, !company.platformFeeEnabled);
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id ? { ...c, platformFeeEnabled: !c.platformFeeEnabled } : c
        )
      );
    } catch (err) {
      console.error("Failed to update platform fee toggle:", err);
    } finally {
      setOpenMenuId(null);
    }
  };

  const handleView = (companyId) => {
    navigate("/superadmin/companies/detail", { state: { companyId } });
  };

  const filterTabs = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "trialing", label: "Trialing" },
    { key: "past_due", label: "Past due" },
    { key: "suspended", label: "Suspended" },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

        html, body { background: rgb(241, 245, 249) !important; }
        .sa-root * { box-sizing: border-box; }
        .sa-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241, 245, 249) !important;
          min-height: 100vh;
        }

        .sa-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease, transform 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        .sa-card:hover { box-shadow: 0 4px 24px rgba(0,0,0,0.09); }

        .sa-metric-card { cursor: default; }
        .sa-metric-card:hover { transform: translateY(-3px); }
        .sa-metric-card .accent-bar {
          position: absolute; top: 0; left: 0; right: 0; height: 4px;
          border-radius: 16px 16px 0 0;
        }

        .sa-search {
          display: flex; align-items: center; gap: 8px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
          padding: 9px 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          width: 320px; transition: border-color 0.15s, box-shadow 0.15s;
        }
        .sa-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .sa-search input {
          border: none; outline: none; background: transparent;
          font-family: 'DM Sans', sans-serif; font-size: 14px; color: #0F172A; width: 100%;
        }
        .sa-search input::placeholder { color: #94A3B8; }

        .sa-tab-row { display: inline-flex; background: #F1F5F9; border-radius: 8px; padding: 3px; gap: 2px; }
        .sa-tab {
          padding: 6px 16px; border-radius: 6px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748B;
          cursor: pointer; border: none; background: transparent;
          transition: background 0.15s, color 0.15s;
        }
        .sa-tab:hover { background: #ffffff; color: #0F172A; }
        .sa-tab.sa-active { background: #ffffff; color: #2563EB; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

        .sa-table { width: 100%; border-collapse: collapse; }
        .sa-table thead tr { background: #F8FAFC; }
        .sa-table th {
          padding: 11px 16px; text-align: left;
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.07em;
          border-bottom: 1px solid #E2E8F0;
        }
        .sa-table td {
          padding: 14px 16px; font-size: 14px; color: #1E293B;
          border-bottom: 1px solid #F1F5F9; vertical-align: middle;
        }
        .sa-table tbody tr { transition: background 0.12s; }
        .sa-table tbody tr:hover { background: #F8FAFC; }
        .sa-table tbody tr:last-child td { border-bottom: none; }

        .sa-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }

        .sa-page-btn {
          display: inline-flex; align-items: center; gap: 6px; padding: 7px 16px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .sa-page-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .sa-page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .sa-page-btn:disabled:hover { border-color: #E2E8F0; background: #ffffff; color: #0F172A; }

        .sa-dl-btn {
          display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B;
          cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .sa-dl-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }

        .sa-count-pill {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 10px; background: #EFF6FF; border-radius: 20px;
          font-size: 12px; font-weight: 700; color: #2563EB;
        }

        .sa-pie-value { font-family: 'DM Mono', monospace; font-size: 22px; font-weight: 500; color: #0F172A; }

        .sa-name-cell { font-weight: 600; color: #0F172A; }
        .sa-sub-cell { font-size: 12px; color: #94A3B8; margin-top: 2px; }

        .sa-menu-wrap { position: relative; }
        .sa-menu-btn {
          padding: 6px; border-radius: 8px; border: none; background: transparent;
          cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
          transition: background 0.15s;
        }
        .sa-menu-btn:hover { background: #F1F5F9; }
        .sa-menu {
          position: absolute; top: calc(100% + 4px); right: 0; width: 160px;
          background: #ffffff; border: 1px solid #E2E8F0; border-radius: 10px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.12); z-index: 50; overflow: hidden;
        }
        .sa-menu-item {
          padding: 10px 14px; font-size: 13px; font-weight: 500; color: #0F172A;
          cursor: pointer; transition: background 0.12s; background: transparent; border: none;
          width: 100%; text-align: left;
        }
        .sa-menu-item:hover { background: #F8FAFC; }
        .sa-menu-item.sa-danger { color: #DC2626; }
      `}</style>

      <div className="sa-root w-full overflow-visible" style={{ background: "rgb(241, 245, 249)", minHeight: "100vh" }}>
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0F172A" }}>
                Welcome back, {user?.name || "Admin"} 👋
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
                All 6ELA customer companies
              </p>
            </div>
            <div className="sa-search">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search company, email, or account no."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                background: "#FEF2F2",
                border: "1px solid #FCA5A5",
                color: "#DC2626",
                borderRadius: 12,
                padding: "12px 16px",
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {/* Metric cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
            {metricCards.map((card) => (
              <div key={card.label} className="sa-card sa-metric-card" style={{ padding: 24 }}>
                <div className="accent-bar" style={{ background: card.accentBar }} />
                <div style={{ marginTop: 8 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: card.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 18,
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    {card.icon}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 6,
                    }}
                  >
                    {card.label}
                  </div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 28, fontWeight: 500, color: "#0F172A", lineHeight: 1.1 }}>
                    {card.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Companies table */}
          <div className="sa-card" style={{ overflow: "hidden" }}>
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Companies</span>
                <span className="sa-count-pill">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563EB", display: "inline-block" }} />
                  {filteredCompanies.length} companies
                </span>
              </div>
              <button className="sa-dl-btn" onClick={openDownloadModalTables}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Download
              </button>
            </div>

            <div
              style={{
                padding: "12px 24px",
                borderBottom: "1px solid #F1F5F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#FAFBFD",
              }}
            >
              <div className="sa-tab-row">
                {filterTabs.map((tab) => (
                  <button
                    key={tab.key}
                    className={`sa-tab${statusFilter === tab.key ? " sa-active" : ""}`}
                    onClick={() => setStatusFilter(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table className="sa-table">
                <thead>
                  <tr>
                    {["Company", "Account type", "Subscription", "Licenses", "Signed up", "Action"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>
                        Loading companies…
                      </td>
                    </tr>
                  ) : currentCompanies.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>
                        No companies match your filters.
                      </td>
                    </tr>
                  ) : (
                    currentCompanies.map((company) => {
                      const statusLabel = company.isSuspended
                        ? "Suspended"
                        : SUBSCRIPTION_STATUS_LABELS[company.subscriptionStatus] || company.subscriptionStatus;
                      const statStyle = STATUS_COLOR_MAP[statusLabel] || { bg: "#F1F5F9", color: "#64748B" };

                      return (
                        <tr key={company.id}>
                          <td>
                            <div className="sa-name-cell">{company.companyName}</div>
                            <div className="sa-sub-cell">{company.companyEmail}</div>
                          </td>
                          <td style={{ textTransform: "capitalize" }}>
                            {company.accountType?.replace("_", " ")}
                          </td>
                          <td>
                            <span className="sa-badge" style={{ background: statStyle.bg, color: statStyle.color }}>
                              ● {statusLabel}
                            </span>
                            <div
                              style={{
                                marginTop: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                color: company.platformFeeEnabled ? "#7C3AED" : "#94A3B8",
                              }}
                            >
                              Platform fee: {company.platformFeeEnabled ? "On" : "Off"}
                            </div>
                          </td>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                            {company.usedLicenses} / {company.maxLicenses}
                          </td>
                          <td>{company.signedUp}</td>
                          <td>
                            <div className="sa-menu-wrap">
                              <button
                                className="sa-menu-btn"
                                onClick={() => setOpenMenuId(openMenuId === company.id ? null : company.id)}
                              >
                                <MoreVertical size={18} color="#64748B" />
                              </button>
                              {openMenuId === company.id && (
                                <div className="sa-menu">
                                  <button className="sa-menu-item" onClick={() => handleView(company.id)}>
                                    View
                                  </button>
                                  <button
                                    className={`sa-menu-item${company.isSuspended ? "" : " sa-danger"}`}
                                    onClick={() => handleToggleSuspend(company)}
                                  >
                                    {company.isSuspended ? "Reinstate" : "Suspend"}
                                  </button>
                                  <button
                                    className="sa-menu-item"
                                    onClick={() => handleTogglePlatformFee(company)}
                                  >
                                    {company.platformFeeEnabled ? "Disable platform fee" : "Enable platform fee"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 24px",
                borderTop: "1px solid #E2E8F0",
                background: "#FAFBFD",
              }}
            >
              <button className="sa-page-btn" onClick={handlePreviousPage} disabled={currentPage === 1}>
                ← Previous
              </button>
              <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>
                Page {currentPage} of {totalPages}
              </span>
              <button className="sa-page-btn" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next →
              </button>
            </div>
          </div>

          {/* Pie chart */}
          <div className="sa-card" style={{ padding: 24, maxWidth: 520 }}>
            <div style={{ borderBottom: "1px solid #E2E8F0", paddingBottom: 16, marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>Subscription status</div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>Breakdown across all companies</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
              <div style={{ width: 200, height: 200, flexShrink: 0 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={64}
                      outerRadius={90}
                      dataKey="value"
                      stroke="none"
                      paddingAngle={3}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {pieData.map((entry, i) => (
                  <div key={entry.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: PIE_COLORS[i % PIE_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <div style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>{entry.name}</div>
                      <div className="sa-pie-value">{entry.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {isDownloadModalTablesOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeDownloadModalTables}
        >
          <DownloadModalTables
            onClose={closeDownloadModalTables}
            tableData={filteredCompanies}
            tag="superadmin_companies"
          />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
    </>
  );
};

export default SuperAdminDashboard;